import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip as _Chip,
  Alert,
  CircularProgress,
  Link,
  Autocomplete,
} from '@mui/material';
import { Add, Delete, Edit, Close, DragIndicator } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  pointerWithin,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '../../api/axios';

const TEAM_GROUPS = [
  { value: 'EN_CASE_TEAM', label: 'EN Case Team' },
  { value: 'GOVERNMENT_PROSECUTORS', label: 'Government Prosecutors' },
  { value: 'GOVERNMENT_INVESTIGATORS', label: 'Government Investigators' },
  { value: 'COURT', label: 'Court' },
  { value: 'OTHER', label: 'Other' },
];

interface Contact {
  id: string;
  full_name: string;
  display_email: string;
  display_phone?: string;
  organization?: string;
  title?: string;
  can_login: boolean;
}

interface Role {
  id: string;
  name: string;
  is_system: boolean;
  display_order: number;
}

interface TeamMember {
  id: string;
  contact: Contact;
  role: Role | null;
  notes?: string;
  team_group: string;
  can_receive_notifications: boolean;
  display_order: number;
}

interface CaseTeamProps {
  caseId: string;
  headers: Record<string, string>;
}

interface SortableTeamMemberRowProps {
  member: TeamMember;
  onEdit: (member: TeamMember) => void;
  onDelete: (member: TeamMember) => void;
}

function SortableTeamMemberRow({ member, onEdit, onDelete }: SortableTeamMemberRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: member.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell sx={{ width: 40, cursor: 'grab' }} {...attributes} {...listeners}>
        <DragIndicator color="action" />
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {member.contact.full_name}
        </Typography>
      </TableCell>
      <TableCell>{member.role?.name || '-'}</TableCell>
      <TableCell>{member.contact.organization || '-'}</TableCell>
      <TableCell>{member.contact.display_email}</TableCell>
      <TableCell>{member.contact.display_phone || '-'}</TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {member.notes || '-'}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <IconButton
          size="small"
          onClick={() => onEdit(member)}
          color="primary"
          sx={{ mr: 1 }}
        >
          <Edit fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onDelete(member)}
          color="error"
        >
          <Delete fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

function DroppableEmptyGroup({ groupValue }: { groupValue: string }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `empty-${groupValue}`,
  });

  return (
    <TableRow ref={setNodeRef}>
      <TableCell
        colSpan={8}
        sx={{
          textAlign: 'center',
          py: 3,
          color: isOver ? 'primary.main' : 'text.secondary',
          fontStyle: 'italic',
          backgroundColor: isOver ? '#e3f2fd' : 'transparent',
          transition: 'background-color 0.2s, color 0.2s',
          border: isOver ? '2px dashed #1976d2' : 'none',
          fontWeight: isOver ? 'bold' : 'normal'
        }}
      >
        {isOver ? 'Drop to move here' : 'No members in this group'}
      </TableCell>
    </TableRow>
  );
}

function DroppableGroupHeader({ group, isEmpty }: { group: { value: string; label: string }; isEmpty: boolean }) {
  const { setNodeRef } = useDroppable({
    id: `header-${group.value}`,
  });

  return (
    <TableRow ref={setNodeRef}>
      <TableCell
        colSpan={8}
        sx={{
          backgroundColor: '#f5f5f5',
          fontWeight: 'bold',
          fontSize: '1rem',
          py: 1.5
        }}
      >
        {group.label}
        {isEmpty && (
          <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
            (Empty - drag members here)
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
}

export default function CaseTeam({ caseId, headers }: CaseTeamProps) {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [memberToDelete, setMemberToDelete] = useState<TeamMember | null>(null);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [selectedTeamGroup, setSelectedTeamGroup] = useState('OTHER');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  // New role creation state
  const [showNewRoleInput, setShowNewRoleInput] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [creatingRole, setCreatingRole] = useState(false);
  const [suggestedRoleInfo, setSuggestedRoleInfo] = useState<string>('');

  // New contact creation state
  const [showNewContactInput, setShowNewContactInput] = useState(false);
  const [newContactFirstName, setNewContactFirstName] = useState('');
  const [newContactLastName, setNewContactLastName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [newContactOrganization, setNewContactOrganization] = useState('');
  const [newContactTitle, setNewContactTitle] = useState('');
  const [creatingContact, setCreatingContact] = useState(false);

  useEffect(() => {
    fetchTeamMembers();
    fetchContacts();
    fetchRoles();
  }, [caseId]);

  const fetchTeamMembers = async () => {
    try {
      const response = await api.get(`/cases/${caseId}/team/`, { headers });

      // Handle different response formats
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || []);

      setTeamMembers(data);
    } catch (err) {
      console.error('Failed to fetch team members:', err);
      setError('Failed to load team members');
      setTeamMembers([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await api.get(`/contacts/?exclude_case=${caseId}`, { headers });

      // Handle different response formats
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || []);

      setContacts(data);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      setContacts([]); // Set empty array on error
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles/', { headers });

      // Handle different response formats
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || []);

      setRoles(data);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
      setRoles([]); // Set empty array on error
    }
  };

  const handleAddTeamMember = async () => {
    if (!selectedContactId) {
      setError('Please select a contact');
      return;
    }

    try {
      await api.post(
        `/cases/${caseId}/team/`,
        {
          contact_id: selectedContactId,
          role_id: selectedRoleId || null,
          team_group: selectedTeamGroup,
          notes: notes,
        },
        { headers }
      );

      // Reset form
      setSelectedContactId('');
      setSelectedRoleId('');
      setSelectedTeamGroup('OTHER');
      setNotes('');
      setAddDialogOpen(false);
      setError('');

      // Refresh team members and contacts list
      await fetchTeamMembers();
      await fetchContacts();
    } catch (err: any) {
      console.error('Failed to add team member:', err);
      setError(err.response?.data?.detail || 'Failed to add team member');
    }
  };

  const handleEditTeamMember = (member: TeamMember) => {
    setEditingMember(member);
    setSelectedRoleId(member.role?.id || '');
    setSelectedTeamGroup(member.team_group || 'OTHER');
    setNotes(member.notes || '');
    setEditDialogOpen(true);
  };

  const handleUpdateTeamMember = async () => {
    if (!editingMember) return;

    try {
      await api.patch(
        `/cases/${caseId}/team/${editingMember.id}/`,
        {
          role_id: selectedRoleId || null,
          team_group: selectedTeamGroup,
          notes: notes,
        },
        { headers }
      );

      // Reset form
      setEditingMember(null);
      setSelectedRoleId('');
      setSelectedTeamGroup('OTHER');
      setNotes('');
      setEditDialogOpen(false);
      setError('');

      // Refresh team members
      await fetchTeamMembers();
    } catch (err: any) {
      console.error('Failed to update team member:', err);
      setError(err.response?.data?.detail || 'Failed to update team member');
    }
  };

  const handleDeleteClick = (member: TeamMember) => {
    setMemberToDelete(member);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setMemberToDelete(null);
    setDeleteConfirmOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!memberToDelete) return;

    try {
      await api.delete(`/cases/${caseId}/team/${memberToDelete.id}/`, { headers });
      setDeleteConfirmOpen(false);
      setMemberToDelete(null);
      await fetchTeamMembers();
      await fetchContacts();
    } catch (err) {
      console.error('Failed to remove team member:', err);
      setError('Failed to remove team member');
    }
  };

  const handleContactChange = async (contactId: string) => {
    setSelectedContactId(contactId);
    setSuggestedRoleInfo('');

    if (!contactId) {
      return;
    }

    // Fetch the contact's most recent role
    try {
      const response = await api.get(`/contacts/${contactId}/recent-role/`, { headers });

      if (response.status === 200 && response.data) {
        const recentRole: Role = response.data;
        setSelectedRoleId(recentRole.id);
        setSuggestedRoleInfo(`Previously: ${recentRole.name}`);
      } else {
        // No previous role found (204 status)
        setSelectedRoleId('');
        setSuggestedRoleInfo('');
      }
    } catch (err) {
      console.error('Failed to fetch recent role:', err);
      // Don't show error to user, just skip the suggestion
      setSelectedRoleId('');
      setSuggestedRoleInfo('');
    }
  };

  const handleCreateNewRole = async () => {
    if (!newRoleName.trim()) {
      setError('Please enter a role name');
      return;
    }

    // Check for duplicate role
    const roleName = newRoleName.trim().toLowerCase();
    const duplicateRole = roles.find((r) => r.name.toLowerCase() === roleName);

    if (duplicateRole) {
      setError(
        `A role named "${duplicateRole.name}" already exists. Please select it from the dropdown instead.`
      );
      return;
    }

    // Calculate next display_order (max + 1)
    const maxDisplayOrder = roles.length > 0
      ? Math.max(...roles.map((r) => r.display_order))
      : 0;
    const nextDisplayOrder = maxDisplayOrder + 1;

    setCreatingRole(true);
    try {
      const response = await api.post(
        '/roles/',
        {
          name: newRoleName.trim(),
          display_order: nextDisplayOrder
        },
        { headers }
      );

      // Add new role to the roles list
      const newRole: Role = response.data;
      setRoles([...roles, newRole]);

      // Select the newly created role
      setSelectedRoleId(newRole.id);

      // Reset new role state
      setNewRoleName('');
      setShowNewRoleInput(false);
      setError('');
    } catch (err: any) {
      console.error('Failed to create role:', err);

      // Parse backend error message
      let errorMessage = 'Failed to create role';
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        } else if (err.response.data.name) {
          // Django validation errors often come back as field-specific errors
          errorMessage = Array.isArray(err.response.data.name)
            ? err.response.data.name[0]
            : err.response.data.name;
        }

        // Check for duplicate constraint error in the message
        if (errorMessage.includes('duplicate') || errorMessage.includes('already exists')) {
          errorMessage = `A role with this name already exists. Please use a different name.`;
        }
      }

      setError(errorMessage);
    } finally {
      setCreatingRole(false);
    }
  };

  const handleCancelNewRole = () => {
    setNewRoleName('');
    setShowNewRoleInput(false);
    setError('');
  };

  const handleCreateNewContact = async () => {
    if (!newContactFirstName.trim() || !newContactLastName.trim()) {
      setError('Please enter first and last name');
      return;
    }

    setCreatingContact(true);

    // Check for duplicate contacts in ALL contacts (not just filtered list)
    const firstName = newContactFirstName.trim().toLowerCase();
    const lastName = newContactLastName.trim().toLowerCase();
    const email = newContactEmail.trim().toLowerCase();

    try {
      // Fetch ALL contacts (without exclude_case filter) to check for duplicates
      const allContactsResponse = await api.get('/contacts/', { headers });
      const allContacts = Array.isArray(allContactsResponse.data)
        ? allContactsResponse.data
        : (allContactsResponse.data?.results || []);

      // Check by email if provided
      if (email) {
        const duplicateByEmail = allContacts.find(
          (c: Contact) => c.display_email.toLowerCase() === email
        );
        if (duplicateByEmail) {
          setError(
            `A contact with email "${email}" already exists: ${duplicateByEmail.full_name}.`
          );
          setCreatingContact(false);
          return;
        }
      }

      // Check by name combination
      const duplicateByName = allContacts.find((c: Contact) => {
        const contactFullName = c.full_name.toLowerCase();
        const searchName = `${firstName} ${lastName}`;
        return contactFullName === searchName;
      });

      if (duplicateByName) {
        setError(
          `A contact named "${duplicateByName.full_name}" already exists.`
        );
        setCreatingContact(false);
        return;
      }
    } catch (err) {
      console.error('Failed to check for duplicate contacts:', err);
      // Continue with creation if duplicate check fails
    }
    try {
      const response = await api.post(
        '/contacts/',
        {
          first_name: newContactFirstName.trim(),
          last_name: newContactLastName.trim(),
          email: newContactEmail.trim() || '',
          phone: newContactPhone.trim() || '',
          organization: newContactOrganization.trim() || '',
          title: newContactTitle.trim() || '',
        },
        { headers }
      );

      // Add new contact to the contacts list
      const newContact: Contact = response.data;
      setContacts([...contacts, newContact]);

      // Select the newly created contact and fetch their role
      setSelectedContactId(newContact.id);
      await handleContactChange(newContact.id);

      // Reset new contact state
      setNewContactFirstName('');
      setNewContactLastName('');
      setNewContactEmail('');
      setNewContactPhone('');
      setNewContactOrganization('');
      setNewContactTitle('');
      setShowNewContactInput(false);
      setError('');
    } catch (err: any) {
      console.error('Failed to create contact:', err);
      setError(err.response?.data?.detail || 'Failed to create contact');
    } finally {
      setCreatingContact(false);
    }
  };

  const handleCancelNewContact = () => {
    setNewContactFirstName('');
    setNewContactLastName('');
    setNewContactEmail('');
    setNewContactPhone('');
    setNewContactOrganization('');
    setNewContactTitle('');
    setShowNewContactInput(false);
    setError('');
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragEndEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    setOverId(event.over?.id as string | null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverId(null);
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);
    setOverId(null);

    if (!over) return;

    const draggedMemberId = active.id as string;
    const draggedMemberIndex = teamMembers.findIndex((member) => member.id === draggedMemberId);
    const draggedMember = teamMembers[draggedMemberIndex];

    // Check if dropped on a group header or empty group placeholder
    const overIdStr = over.id as string;
    if (overIdStr.startsWith('header-') || overIdStr.startsWith('empty-')) {
      const targetGroupValue = overIdStr.replace('header-', '').replace('empty-', '');

      // Find the max display_order in the target group to add at the end
      const targetGroupMembers = teamMembers.filter(m => m.team_group === targetGroupValue);
      const maxOrder = targetGroupMembers.length > 0
        ? Math.max(...targetGroupMembers.map(m => m.display_order))
        : -1;
      const newDisplayOrder = maxOrder + 1;

      // Update the member's group and display_order
      const updatedMembers = teamMembers.map((member) =>
        member.id === draggedMemberId
          ? { ...member, team_group: targetGroupValue, display_order: newDisplayOrder }
          : member
      );

      setTeamMembers(updatedMembers);

      // Update the backend
      api.patch(
        `/cases/${caseId}/team/${draggedMemberId}/`,
        { team_group: targetGroupValue, display_order: newDisplayOrder },
        { headers }
      )
        .then(() => {
          console.log('Team member moved to new group successfully');
        })
        .catch((error: any) => {
          console.error('Failed to update team member:', error);
          setError(`Failed to save changes: ${error.response?.data?.detail || error.message}`);
          fetchTeamMembers();
        });

      return;
    }

    // Regular drag and drop between members
    if (active.id !== over.id) {
      const oldIndex = draggedMemberIndex;
      const newIndex = teamMembers.findIndex((member) => member.id === over.id);

      const targetMember = teamMembers[newIndex];

      // Check if the dragged member moved to a different group
      const draggedMemberNewGroup = targetMember.team_group;
      const didChangeGroup = draggedMember.team_group !== draggedMemberNewGroup;

      if (didChangeGroup) {
        // Moving to a different group - add to the END of that group
        const targetGroupMembers = teamMembers.filter(m => m.team_group === draggedMemberNewGroup);
        const maxOrder = targetGroupMembers.length > 0
          ? Math.max(...targetGroupMembers.map(m => m.display_order))
          : -1;
        const newDisplayOrder = maxOrder + 1;

        // Update the member's group and display_order
        const updatedMembers = teamMembers.map((member) =>
          member.id === draggedMemberId
            ? { ...member, team_group: draggedMemberNewGroup, display_order: newDisplayOrder }
            : member
        );

        setTeamMembers(updatedMembers);

        // Update the backend
        api.patch(
          `/cases/${caseId}/team/${draggedMemberId}/`,
          { team_group: draggedMemberNewGroup, display_order: newDisplayOrder },
          { headers }
        )
          .then(() => {
            console.log('Team member moved to new group successfully');
          })
          .catch((error: any) => {
            console.error('Failed to update team member:', error);
            setError(`Failed to save changes: ${error.response?.data?.detail || error.message}`);
            fetchTeamMembers();
          });
      } else {
        // Same group - reorder within the group
        const newOrder = arrayMove(teamMembers, oldIndex, newIndex);

        // Update display_order
        const newOrderWithUpdates = newOrder.map((member, index) => ({
          ...member,
          display_order: index
        }));

        // Optimistically update local state immediately
        setTeamMembers(newOrderWithUpdates);

        // Build the orders array for the API
        const orders = newOrderWithUpdates.map((member, index) => ({
          member_id: member.id,
          order: index
        }));

        // Update order for all members
        api.patch(
          `/cases/${caseId}/team/update_order/`,
          { orders },
          { headers }
        )
          .then(() => {
            console.log('Team member order updated successfully');
          })
          .catch((error: any) => {
            console.error('Failed to update team member order:', error);
            setError(`Failed to save order: ${error.response?.data?.detail || error.message}`);
            // Revert on error - refetch team members
            fetchTeamMembers();
          });
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          👥 Case Team
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            // Reset form state when opening dialog
            setSelectedContactId('');
            setSelectedRoleId('');
            setNotes('');
            setSuggestedRoleInfo('');
            setShowNewContactInput(false);
            setShowNewRoleInput(false);
            setError('');
            setAddDialogOpen(true);
          }}
        >
          Add Team Member
        </Button>
      </Box>

      {error && !addDialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <DragOverlay>
          {activeId ? (
            <TableContainer component={Paper} sx={{ width: 'auto', boxShadow: 3 }}>
              <Table>
                <TableBody>
                  {(() => {
                    const member = teamMembers.find(m => m.id === activeId);
                    if (!member) return null;
                    return (
                      <TableRow sx={{ opacity: 0.9 }}>
                        <TableCell sx={{ width: 40 }}>
                          <DragIndicator color="action" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {member.contact.full_name}
                          </Typography>
                        </TableCell>
                        <TableCell>{member.role?.name || '-'}</TableCell>
                        <TableCell>{member.contact.organization || '-'}</TableCell>
                        <TableCell>{member.contact.display_email}</TableCell>
                        <TableCell>{member.contact.display_phone || '-'}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {member.notes || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="primary" sx={{ mr: 1 }}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error">
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </DragOverlay>
        {teamMembers.length === 0 ? (
          <Alert severity="info">
            No team members assigned yet. Click "Add Team Member" to get started.
          </Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: '#616161' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 40 }}></TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Role</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Organization</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Phone</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Notes</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {TEAM_GROUPS.map((group) => {
                  const groupMembers = teamMembers.filter((m) => m.team_group === group.value);

                  // Check if we're dragging over this group
                  const isDraggingOver = overId && (
                    overId === `header-${group.value}` ||
                    overId === `empty-${group.value}` ||
                    groupMembers.some(m => m.id === overId)
                  );

                  const draggedMember = activeId ? teamMembers.find(m => m.id === activeId) : null;
                  const shouldShowPlaceholder = isDraggingOver && draggedMember && draggedMember.team_group !== group.value;

                  return (
                    <React.Fragment key={group.value}>
                      {/* Group Header Row */}
                      <DroppableGroupHeader
                        group={group}
                        isEmpty={groupMembers.length === 0}
                      />
                      {/* Group Members */}
                      {groupMembers.length === 0 ? (
                        <DroppableEmptyGroup groupValue={group.value} />
                      ) : (
                        <>
                          <SortableContext
                            items={groupMembers.map((member) => member.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {groupMembers.map((member) => (
                              <SortableTeamMemberRow
                                key={member.id}
                                member={member}
                                onEdit={handleEditTeamMember}
                                onDelete={handleDeleteClick}
                              />
                            ))}
                          </SortableContext>
                          {shouldShowPlaceholder && (
                            <TableRow sx={{ backgroundColor: '#e3f2fd', opacity: 0.6 }}>
                              <TableCell colSpan={8} sx={{ py: 2, textAlign: 'center', fontStyle: 'italic', color: 'primary.main' }}>
                                Drop to move here
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DndContext>

      {/* Add Team Member Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => {
          // Reset form state when closing dialog
          setSelectedContactId('');
          setSelectedRoleId('');
          setNotes('');
          setSuggestedRoleInfo('');
          setShowNewContactInput(false);
          setShowNewRoleInput(false);
          setNewContactFirstName('');
          setNewContactLastName('');
          setNewContactEmail('');
          setNewContactPhone('');
          setNewContactOrganization('');
          setNewContactTitle('');
          setNewRoleName('');
          setError('');
          setAddDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add Team Member
          <IconButton
            onClick={() => setAddDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {!showNewContactInput ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Autocomplete
                  sx={{ width: '70%' }}
                  options={[...contacts].sort((a, b) => a.full_name.localeCompare(b.full_name))}
                  getOptionLabel={(option) =>
                    `${option.full_name}${option.organization ? ` (${option.organization})` : ''}`
                  }
                  value={contacts.find((c) => c.id === selectedContactId) || null}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      handleContactChange(newValue.id);
                    } else {
                      setSelectedContactId('');
                      setSelectedRoleId('');
                      setSuggestedRoleInfo('');
                    }
                  }}
                  renderInput={(params) => <TextField {...params} label="Contact" />}
                />
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setShowNewContactInput(true)}
                  sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  + New Contact
                </Link>
              </Box>
            ) : (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  New Contact Information
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="First Name *"
                    value={newContactFirstName}
                    onChange={(e) => setNewContactFirstName(e.target.value)}
                    fullWidth
                    autoFocus
                  />
                  <TextField
                    label="Last Name *"
                    value={newContactLastName}
                    onChange={(e) => setNewContactLastName(e.target.value)}
                    fullWidth
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <TextField
                    label="Email"
                    value={newContactEmail}
                    onChange={(e) => setNewContactEmail(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Phone"
                    value={newContactPhone}
                    onChange={(e) => setNewContactPhone(e.target.value)}
                    fullWidth
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <TextField
                    label="Organization"
                    value={newContactOrganization}
                    onChange={(e) => setNewContactOrganization(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Title"
                    value={newContactTitle}
                    onChange={(e) => setNewContactTitle(e.target.value)}
                    fullWidth
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleCreateNewContact}
                    disabled={creatingContact}
                  >
                    {creatingContact ? 'Creating...' : 'Create Contact'}
                  </Button>
                  <Button size="small" onClick={handleCancelNewContact}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}

            {!showNewContactInput && (
              <>
                {suggestedRoleInfo && (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    {suggestedRoleInfo}
                  </Alert>
                )}

                {!showNewRoleInput ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Autocomplete
                      sx={{ width: '70%' }}
                      options={roles}
                      getOptionLabel={(option) => option.name}
                      value={roles.find((r) => r.id === selectedRoleId) || null}
                      onChange={(_, newValue) => {
                        if (newValue) {
                          setSelectedRoleId(newValue.id);
                        } else {
                          setSelectedRoleId('');
                        }
                      }}
                      renderInput={(params) => <TextField {...params} label="Role" />}
                    />
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setShowNewRoleInput(true)}
                      sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      + New Role
                    </Link>
                  </Box>
                ) : (
                  <Box>
                    <TextField
                      fullWidth
                      label="New Role Name"
                      value={newRoleName}
                      onChange={(e) => setNewRoleName(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleCreateNewRole();
                        }
                      }}
                      autoFocus
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleCreateNewRole}
                        disabled={creatingRole}
                      >
                        {creatingRole ? 'Creating...' : 'Create Role'}
                      </Button>
                      <Button size="small" onClick={handleCancelNewRole}>
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}

                <FormControl fullWidth>
                  <InputLabel>Team Group</InputLabel>
                  <Select
                    value={selectedTeamGroup}
                    label="Team Group"
                    onChange={(e) => setSelectedTeamGroup(e.target.value)}
                  >
                    {TEAM_GROUPS.map((group) => (
                      <MenuItem key={group.value} value={group.value}>
                        {group.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddTeamMember} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Team Member Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Team Member
          <IconButton
            onClick={() => setEditDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {editingMember && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Editing: <strong>{editingMember.contact.full_name}</strong>
              </Alert>
            )}

            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedRoleId}
                label="Role"
                onChange={(e) => setSelectedRoleId(e.target.value)}
              >
                <MenuItem value="">
                  <em>(No Role)</em>
                </MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Team Group</InputLabel>
              <Select
                value={selectedTeamGroup}
                label="Team Group"
                onChange={(e) => setSelectedTeamGroup(e.target.value)}
              >
                {TEAM_GROUPS.map((group) => (
                  <MenuItem key={group.value} value={group.value}>
                    {group.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateTeamMember} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirm Delete
          <IconButton
            onClick={handleDeleteCancel}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {memberToDelete && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Are you sure you want to remove this team member from the case?
              </Alert>
              <Typography variant="body1">
                <strong>{memberToDelete.contact.full_name}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Role: {memberToDelete.role?.name || 'None'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}